
import {
  Component, Vue, Prop,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  LeadType, Operation, OperationChild,
} from '@/types';
import { isTaskCompleted, isTaskFailed, currentUserHasPermission } from '@/helpers';

moment.locale('it');

@Component
export default class InstallationDateMixin extends Vue {
  @Prop(Object) readonly operation!: Operation;
  @Prop(Number) readonly childId!: number;

  get operationChild(): OperationChild | null {
    return this.operation.children
      && this.childId in this.operation.children
      && this.operation.children[this.childId]
      ? this.operation.children[this.childId] : null;
  }

  get childProjectType(): LeadType | null {
    if (!this.operationChild) return null;
    return this.operationChild.type;
  }

  get partialInstallationDate(): string | null {
    if (this.partialInstallationDateMode === 'single') {
      return this.operationChild?.partialInstallationDate
        && moment(this.operationChild.partialInstallationDate).isValid()
        ? moment(this.operationChild.partialInstallationDate).format('DD/MM/YYYY')
        : null;
    }

    // 'week'
    return this.operationChild?.partialInstallationDate
      && moment(this.operationChild.partialInstallationDate).isValid()
      ? `WW ${moment(this.operationChild.partialInstallationDate).format('WW-GGGG')}`
      : null;
  }

  get partialInstallationDateMode(): 'single' | 'week' {
    // Modalità di visualizzazione della data (settimanale di default)
    return this.operationChild?.partialInstallationDateMode ?? 'week';
  }

  get partialInstallationDateConfirm(): boolean {
    return this.operationChild?.partialInstallationDateConfirm === true;
  }

  get partialInstallationCompleted(): boolean {
    return this.operationChild?.partialInstallationCompleted === true;
  }

  get installationDate(): string | null {
    if (this.installationDateMode === 'single') {
      return this.operationChild?.installationDate
        && moment(this.operationChild.installationDate).isValid()
        ? moment(this.operationChild.installationDate).format('DD/MM/YYYY')
        : null;
    }

    // 'week'
    return this.operationChild?.installationDate
      && moment(this.operationChild.installationDate).isValid()
      ? `WW ${moment(this.operationChild.installationDate).format('WW-GGGG')}`
      : null;
  }

  get installationDateMode(): 'single' | 'week' {
    // Modalità di visualizzazione della data (settimanale di default)
    return this.operationChild?.installationDateMode ?? 'week';
  }

  get installationDateConfirm(): boolean {
    return this.operationChild?.installationDateConfirm === true;
  }

  get installationCompleted(): boolean {
    return this.operationChild?.installationCompleted === true;
  }

  get installationNotes(): string | null {
    return this.operationChild?.installationNotes || null;
  }

  get dateText(): string {
    // È impostata una data di installazione finale
    if (this.installationDate) {
      // Se non è impostata una data di installazione parziale...
      if (!this.partialInstallationDate) {
        // ... ritorna la data di installazione finale
        return this.installationDate;
      }

      // Se è impostata una data di installazione parziale
      // e l'installazione parziale non è completata,
      // ritorna quella parziale, altrimenti quella finale.
      return this.partialInstallationCompleted
        ? this.installationDate
        : this.partialInstallationDate;
    }

    if (this.partialInstallationDate) return this.partialInstallationDate;

    return 'da definire';
  }

  get dateClass(): string {
    if (this.installationDate || this.partialInstallationDate) return '';
    return 'grey--text text--lighten-2';
  }

  get tooltipColor(): string {
    if (this.installationCompleted) return 'success';
    if (this.installationDateConfirm) return '';
    if (this.installationDate) return '';
    if (this.partialInstallationCompleted) return 'success';
    if (this.partialInstallationDateConfirm) return '';
    if (this.partialInstallationDate) return '';
    return 'error';
  }

  get iconTooltipText() {
    let text = '';

    if (this.installationCompleted) {
      text += this.partialInstallationCompleted
        ? 'Installazione parziale e finale completata'
        : 'Installazione finale completata';
    } else

    if (this.installationDate
      && (this.partialInstallationCompleted || !this.partialInstallationDate)
    ) {
      text += this.installationDateConfirm
        ? 'Installazione finale concordata'
        : 'Installazione finale non ancora concordata';
    } else

    if (this.partialInstallationCompleted) {
      text += 'Installazione parziale completata';
    } else if (this.partialInstallationDate) {
      text += this.partialInstallationDateConfirm
        ? 'Installazione parziale concordata'
        : 'Installazione parziale non ancora concordata';
    } else {
      text += 'Installazione non ancora concordata';
    }

    return text;
  }

  get tooltipText() {
    let text = '';

    if (this.installationCompleted) {
      text += this.partialInstallationCompleted
        ? 'Installazione parziale e finale completata'
        : 'Installazione finale completata';
    } else

    if (this.installationDate
      && (this.partialInstallationCompleted || !this.partialInstallationDate)
    ) {
      text += this.installationDateConfirm
        ? 'Data concordata per l\'installazione finale'
        : 'Data prevista per l\'installazione finale (non confermata)';
    } else

    if (this.partialInstallationCompleted) {
      text += 'Installazione parziale completata';
    } else if (this.partialInstallationDate) {
      text += this.partialInstallationDateConfirm
        ? 'Data concordata per l\'installazione parziale'
        : 'Data prevista per l\'installazione parziale (non confermata)';
    } else {
      text += 'Data prevista per l\'installazione da definire';
    }

    if (this.installationNotes && currentUserHasPermission('taski.installationNotes.show')) text += `<br>${this.installationNotes}`;

    return text;
  }

  get iconColor(): string {
    if (this.installationDate || this.partialInstallationDate) return 'statusDone';
    return 'grey lighten-2';
  }

  get icon(): string {
    // Se ancora non sono state impostate date...
    if (!this.partialInstallationDate && !this.installationDate) {
      // ... mostra la batteria vuota
      return 'mdi-battery-outline';
    }

    // Se l'installazione finale è completa...
    if (this.installationCompleted) {
      // ... mostra la batteria piena se non è stata completata anche una parziale,
      // altrimenti mostra la batteria piena divisa a metà.
      return this.partialInstallationCompleted ? 'mdi-battery-minus-variant' : 'mdi-battery';
    }

    // Se l'installazione finale NON è completa
    // e l'installazione parziale non è avvenuta...
    if (!this.installationCompleted
      && !this.partialInstallationDate
      && !this.partialInstallationDateConfirm
      && !this.partialInstallationCompleted) {
      // ... l'installazione finale in corso (data impostata e forse confermata)
      // Mostra la batteria piena
      return 'mdi-battery';
    }

    // L'installazione finale NON è completa
    // e sta avvenendo o è avvenuta l'installazione parziale

    return this.installationDate && this.partialInstallationCompleted ? 'mdi-battery-minus-variant' : 'mdi-battery-50';
  }

  get columnClass(): string {
    if (isTaskCompleted(this.operationChild, 'I')) return 'green';
    if (isTaskFailed(this.operationChild, 'I')) return 'red';

    if (this.installationCompleted) return 'completed';
    if (this.installationDateConfirm) return this.partialInstallationCompleted ? 'yellow-green' : 'yellow';

    if (this.partialInstallationCompleted) return 'white-green';
    if (this.partialInstallationDateConfirm) return 'white-yellow';

    return '';
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }
}
